export interface KeyPress {
	alt: boolean;
	ctrl: boolean;
	key: string;
	meta: boolean;
	mod: boolean;
	shift: boolean;
}

export type ParsedBinding = KeyPress[];

const MODIFIER_FLAG_BY_TOKEN: Record<string, keyof Omit<KeyPress, "key">> = {
	alt: "alt",
	cmd: "meta",
	command: "meta",
	control: "ctrl",
	ctrl: "ctrl",
	meta: "meta",
	mod: "mod",
	opt: "alt",
	option: "alt",
	shift: "shift"
};

const normalizeKeyToken = (token: string): string => {
	if (token === " " || token === "space" || token === "spacebar") return "space";
	return token;
};

const emptyPress = (): KeyPress => ({ alt: false, ctrl: false, key: "", meta: false, mod: false, shift: false });

export const parseBinding = (binding: string): ParsedBinding =>
	binding
		.trim()
		.split(/\s+/)
		.filter(Boolean)
		.map((segment) => {
			const press = emptyPress();
			for (const rawToken of segment.split("+")) {
				const token = rawToken.trim().toLowerCase();
				if (!token) continue;
				const modifierFlag = MODIFIER_FLAG_BY_TOKEN[token];
				if (modifierFlag) press[modifierFlag] = true;
				else press.key = normalizeKeyToken(token);
			}
			return press;
		});

export type KeyEventLike = Pick<KeyboardEvent, "altKey" | "ctrlKey" | "key" | "metaKey" | "shiftKey">;

const normalizeEventKey = (key: string): string => {
	if (key === " ") return "space";
	return key.toLowerCase();
};

export const keyPressMatchesEvent = (press: KeyPress, event: KeyEventLike): boolean => {
	if (press.alt !== event.altKey) return false;
	if (press.shift !== event.shiftKey) return false;
	if (press.mod) {
		if (!(event.ctrlKey || event.metaKey)) return false;
	} else {
		if (press.ctrl !== event.ctrlKey) return false;
		if (press.meta !== event.metaKey) return false;
	}
	return normalizeEventKey(event.key) === press.key;
};

const pressesEqual = (a: KeyPress, b: KeyPress): boolean =>
	a.alt === b.alt &&
	a.ctrl === b.ctrl &&
	a.key === b.key &&
	a.meta === b.meta &&
	a.mod === b.mod &&
	a.shift === b.shift;

const bindingsEqual = (a: ParsedBinding, b: ParsedBinding): boolean =>
	a.length === b.length && a.every((press, index) => pressesEqual(press, b[index]));

const RESERVED_BINDING_STRINGS = [
	"mod+w",
	"ctrl+w",
	"meta+w",
	"mod+t",
	"ctrl+t",
	"meta+t",
	"mod+n",
	"ctrl+n",
	"meta+n",
	"mod+q",
	"ctrl+q",
	"meta+q",
	"ctrl+tab",
	"meta+tab",
	"f11",
	"tab"
];

export const RESERVED_BINDINGS: ParsedBinding[] = RESERVED_BINDING_STRINGS.map(parseBinding);

export const isReservedBinding = (binding: string): boolean => {
	const parsed = parseBinding(binding);
	return RESERVED_BINDINGS.some((reserved) => bindingsEqual(parsed, reserved));
};

export const areBindingsEquivalent = (first: string, second: string): boolean =>
	bindingsEqual(parseBinding(first), parseBinding(second));

const TINYKEYS_MODIFIERS: { flag: keyof KeyPress; token: string }[] = [
	{ flag: "mod", token: "$mod" },
	{ flag: "ctrl", token: "Control" },
	{ flag: "meta", token: "Meta" },
	{ flag: "alt", token: "Alt" },
	{ flag: "shift", token: "Shift" }
];

const keyToTinykeys = (key: string): string => (key === "space" ? "Space" : key);

const pressToTinykeys = (press: KeyPress): string => {
	const parts = TINYKEYS_MODIFIERS.filter(({ flag }) => press[flag]).map(({ token }) => token);
	parts.push(keyToTinykeys(press.key));
	return parts.join("+");
};

export const toTinykeysBinding = (binding: string): string => parseBinding(binding).map(pressToTinykeys).join(" ");

const displayKey = (key: string): string => {
	if (key.length === 1) return key.toUpperCase();
	if (key === "space") return "Space";
	return key.charAt(0).toUpperCase() + key.slice(1);
};

export const formatBindingForDisplay = (binding: string, isMac: boolean): string =>
	parseBinding(binding)
		.map((press) => {
			const parts: string[] = [];
			if (press.mod) parts.push(isMac ? "⌘" : "Ctrl");
			if (press.ctrl) parts.push(isMac ? "⌃" : "Ctrl");
			if (press.meta) parts.push(isMac ? "⌘" : "Meta");
			if (press.alt) parts.push(isMac ? "⌥" : "Alt");
			if (press.shift) parts.push(isMac ? "⇧" : "Shift");
			parts.push(displayKey(press.key));
			return isMac ? parts.join("") : parts.join("+");
		})
		.join(" ");

export const keycapTokens = (binding: string, isMac: boolean): string[] =>
	formatBindingForDisplay(binding, isMac).split(" ").filter(Boolean);

export const detectIsMac = (): boolean => {
	if (typeof navigator === "undefined") return false;
	const platform = navigator.platform ?? "";
	return /mac|iphone|ipad|ipod/i.test(platform) || /mac os/i.test(navigator.userAgent ?? "");
};
